import { useCallback, useMemo, useState } from "react";
import useAuth from "../useAuth";
import { useDebounce } from "../useDebounce";
import { useGetWalletTransactionsQuery } from "../../store";
import { localDayStartISO, localDayEndISO } from "../../utils/localDayRange";

const SEARCH_DEBOUNCE_MS = 400;

/**
 * Ödemeler sekmeleri (Satış / İade, Bakiye Yükleme) — sunucu sayfalaması,
 * debounce'lu arama, tarih aralığı ve sekme filtresi (#242).
 * Filtre/arama değişince sayfa AYNI render'da 1'e döner (çift istek yok);
 * RTK Query isteği argümana göre anahtarlar, eski yanıt yenisini ezmez.
 * Sözleşme: BayinerIOT-Workspace- docs/api-contracts/wallet-transactions-panel.md
 *
 * @param {{ toParams: (activeFilter: string) => { type: string, status?: string }, pageSize?: number }} args
 */
export default function useWalletTransactionList({ toParams, pageSize = 20 }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);

  const filterKey = JSON.stringify([
    activeFilter,
    debouncedSearch,
    localDayStartISO(startDate),
    localDayEndISO(endDate),
  ]);
  // Sayfa filtre anahtarına bağlı: anahtar değişince 1'den başlar
  const [pageState, setPageState] = useState({ key: filterKey, page: 1 });
  const currentPage = pageState.key === filterKey ? pageState.page : 1;
  const setCurrentPage = useCallback(
    (page) => setPageState({ key: filterKey, page }),
    [filterKey]
  );

  const { data, isLoading, isFetching, isError } = useGetWalletTransactionsQuery(
    {
      token,
      ...toParams(activeFilter),
      search: debouncedSearch || undefined,
      startDate: localDayStartISO(startDate),
      endDate: localDayEndISO(endDate),
      page: currentPage,
      limit: pageSize,
    },
    { skip: !token }
  );

  const payload = data?.data;
  const transactions = useMemo(() => payload?.transactions || [], [payload]);

  const clear = useCallback(() => {
    setActiveFilter("all");
    setSearch("");
    setStartDate(null);
    setEndDate(null);
  }, []);

  return {
    transactions,
    total: payload?.total || 0,
    totalPages: payload?.totalPages || 0,
    counts: payload?.counts,
    isLoading,
    isFetching,
    isError,
    activeFilter,
    setActiveFilter,
    search,
    setSearch,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    currentPage,
    setCurrentPage,
    pageSize,
    clear,
  };
}
